import { useTranslation } from "../../i18n/LanguageProvider";
import { useState, useEffect, useCallback } from "react";
import { format } from "date-fns";
import { de, enUS, sk } from "date-fns/locale";
import { Play, Square, Clock, Coffee, MapPin, Navigation, X, Car, Loader2, ChevronDown, Route, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toLocalHHMM } from "./TimeClockHelpers";

const TRIP_API = process.env.REACT_APP_BACKEND_URL + "/api";

function tripFetch(url, opts = {}) {
  const token = localStorage.getItem("token");
  return fetch(url, { ...opts, headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", ...(opts.headers || {}) } });
}

// ─── Navigation Helper ───
function buildNavUrl(project) {
  if (!project) return null;
  const addr = project.address && typeof project.address === "object" ? project.address : null;
  if (addr?.lat && addr?.lng) {
    return `https://www.google.com/maps/dir/?api=1&destination=${addr.lat},${addr.lng}`;
  }
  const parts = [addr?.street, [addr?.zip, addr?.city].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  if (parts) return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(parts)}`;
  return null;
}

// ─── Navigation Popup nach Einstempeln ───
function NavPopup({ project, onClose }) {
  const navUrl = buildNavUrl(project);
  if (!navUrl) return null;
  const projectName = project?.display_name || project?.name || "Baustelle";

  return (
    <div className="fixed inset-0 z-[900] flex items-center justify-center px-4" data-testid="nav-popup">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-[#1a1d27] border border-white/10 rounded-2xl p-6 w-full max-w-sm text-center space-y-4 animate-in zoom-in-95 duration-200">
        <div className="w-14 h-14 bg-blue-500/15 rounded-full flex items-center justify-center mx-auto">
          <Navigation className="w-7 h-7 text-blue-400" />
        </div>
        <div>
          <p className="text-base font-bold text-white">Zur Baustelle navigieren?</p>
          <p className="text-sm text-gray-400 mt-1">{projectName}</p>
        </div>
        <div className="flex gap-3">
          <button onClick={onClose}
            className="flex-1 h-12 bg-white/[0.06] border border-white/10 text-white rounded-xl text-sm font-semibold active:bg-white/10 transition-colors"
            data-testid="nav-popup-no"
          >Nein</button>
          <a href={navUrl} target="_blank" rel="noopener noreferrer" onClick={onClose}
            className="flex-1 h-12 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-sm font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-all"
            data-testid="nav-popup-yes"
          >
            <Navigation className="w-4 h-4" />Ja, navigieren
          </a>
        </div>
      </div>
    </div>
  );
}

// ─── Trip types ───
const TRIP_TYPES = [
  { value: "wohnort_projekt", label: "Wohnort \u2192 Projekt" },
  { value: "projekt_unterkunft", label: "Projekt \u2192 Unterkunft" },
  { value: "unterkunft_projekt", label: "Unterkunft \u2192 Projekt" },
  { value: "projekt_wohnort", label: "Projekt \u2192 Wohnort" },
  { value: "sonstiges", label: "Sonstiges" },
];

// ═══════════════════════════════════════════════════════════
// TRIP SECTION (button + dialog + list — idiotensicher)
// ═══════════════════════════════════════════════════════════
function TripSection() {
  const [trips, setTrips] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [tripType, setTripType] = useState("wohnort_projekt");
  const [km, setKm] = useState("");
  const [saving, setSaving] = useState(false);

  const loadTrips = useCallback(async () => {
    try {
      const res = await tripFetch(`${TRIP_API}/trips`);
      if (res.ok) { const d = await res.json(); setTrips(d.trips || []); }
    } catch {}
  }, []);

  useEffect(() => { loadTrips(); }, [loadTrips]);

  const openDialog = () => {
    setStartTime(""); setEndTime(""); setTripType("wohnort_projekt"); setKm("");
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!startTime || !endTime) { toast.error("Bitte Start- und Endzeit eingeben"); return; }
    setSaving(true);
    try {
      const res = await tripFetch(`${TRIP_API}/trips`, {
        method: "POST",
        body: JSON.stringify({ start_time: startTime, end_time: endTime, type: tripType, km: km ? parseFloat(km) : null }),
      });
      if (res.ok) { toast.success("Fahrt gespeichert"); loadTrips(); setShowDialog(false); }
      else { const err = await res.json().catch(() => ({})); toast.error(err.detail || "Fehler"); }
    } catch { toast.error("Server nicht erreichbar"); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${TRIP_API}/trips/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { toast.success("Geloescht"); loadTrips(); }
    } catch {}
  };

  // Duration preview
  const duration = startTime && endTime ? (() => {
    const [sh, sm] = startTime.split(":").map(Number);
    const [eh, em] = endTime.split(":").map(Number);
    let mins = (eh * 60 + em) - (sh * 60 + sm);
    if (mins < 0) mins += 24 * 60;
    return `${Math.floor(mins / 60)}h ${mins % 60 > 0 ? (mins % 60) + "m" : ""}`.trim();
  })() : null;

  const totalMin = trips.reduce((s, t) => s + (t.duration_minutes || 0), 0);

  return (
    <div className="mt-4" data-testid="trip-section">
      {/* Big Button */}
      <button onClick={openDialog}
        className="w-full h-14 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-sm font-bold flex items-center justify-center gap-2 active:scale-[0.97] transition-all"
        data-testid="btn-add-trip">
        <Plus className="w-5 h-5" />
        Fahrt nachtragen
      </button>

      {/* Trip List */}
      {trips.length > 0 && (
        <div className="mt-3 bg-white/[0.04] border border-white/8 rounded-xl p-3 space-y-2" data-testid="trip-list">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1"><Route className="w-3 h-3 text-blue-400" />Fahrten heute</span>
            <span className="text-[11px] font-mono font-bold text-blue-400">{Math.floor(totalMin / 60)}h {totalMin % 60}m</span>
          </div>
          {trips.map(trip => (
            <div key={trip.id} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-white font-medium">{trip.type_label}</p>
                <p className="text-[10px] text-gray-500 font-mono">{trip.start_time} - {trip.end_time} ({trip.duration_minutes} min){trip.km > 0 ? ` / ${trip.km} km` : ""}</p>
              </div>
              <button onClick={() => handleDelete(trip.id)} className="p-1.5 rounded-lg hover:bg-red-500/15" data-testid={`trip-delete-${trip.id}`}>
                <Trash2 className="w-3.5 h-3.5 text-red-400" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Dialog */}
      {showDialog && (
        <div className="fixed inset-0 z-50 flex items-end justify-center" data-testid="trip-dialog">
          <div className="fixed inset-0 bg-black/70" onClick={() => setShowDialog(false)} />
          <div className="relative w-full max-w-md mx-2 mb-2 rounded-2xl border p-5 space-y-4 animate-in slide-in-from-bottom duration-300"
            style={{ backgroundColor: "var(--tga-bg-card, #1a1d27)", borderColor: "var(--tga-border, #2a2d37)" }}>

            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-white flex items-center gap-2"><Car className="w-5 h-5 text-blue-400" />Fahrt nachtragen</h3>
              <button onClick={() => setShowDialog(false)} className="p-1 rounded-lg hover:bg-white/10"><X className="w-5 h-5 text-gray-400" /></button>
            </div>

            {/* Grund der Fahrt — GROSSE Buttons */}
            <div>
              <p className="text-[11px] font-medium text-gray-400 mb-2">Wohin?</p>
              <div className="space-y-2">
                {TRIP_TYPES.filter(t => t.value !== "sonstiges").map(t => (
                  <button key={t.value} onClick={() => setTripType(t.value)}
                    className={`w-full h-12 rounded-xl border text-sm font-medium transition-all flex items-center justify-center ${tripType === t.value ? "bg-blue-600 border-blue-500 text-white" : "bg-white/[0.04] border-white/10 text-gray-300 hover:border-white/20"}`}
                    data-testid={`trip-type-${t.value}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Zeit */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-medium text-gray-400 mb-1 block">Start</label>
                <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)}
                  className="w-full h-12 px-3 rounded-xl border border-white/10 text-base bg-white/[0.06] text-white outline-none focus:border-blue-500/50 text-center"
                  data-testid="trip-start-time" />
              </div>
              <div>
                <label className="text-[11px] font-medium text-gray-400 mb-1 block">Ende</label>
                <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)}
                  className="w-full h-12 px-3 rounded-xl border border-white/10 text-base bg-white/[0.06] text-white outline-none focus:border-blue-500/50 text-center"
                  data-testid="trip-end-time" />
              </div>
            </div>
            {duration && <div className="text-center"><span className="text-sm font-mono px-4 py-1.5 rounded-full bg-blue-500/15 text-blue-400 font-bold">{duration}</span></div>}

            {/* KM optional */}
            <div>
              <label className="text-[11px] font-medium text-gray-400 mb-1 block">Kilometer (optional)</label>
              <input type="number" min="0" step="1" placeholder="z.B. 45" value={km} onChange={e => setKm(e.target.value)}
                className="w-full h-11 px-3 rounded-xl border border-white/10 text-sm bg-white/[0.06] text-white outline-none focus:border-blue-500/50"
                data-testid="trip-km-input" />
            </div>

            {/* Save */}
            <button onClick={handleSave} disabled={saving || !startTime || !endTime}
              className="w-full h-14 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white rounded-xl text-base font-bold flex items-center justify-center gap-2 active:scale-[0.97] transition-all"
              data-testid="trip-save-btn">
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Car className="w-5 h-5" />}
              Fahrt speichern
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Mobile Quick Mode Component - Dark Theme, compact layout
export function MobileQuickMode({ 
  currentTime, clockStatus, clockedInAt, projects, selectedProject, setSelectedProject,
  selectedBreakMinutes, setSelectedBreakMinutes, handleClockIn, handleClockOut, user, todayEntry,
  showNavPopup, navProject, onCloseNavPopup,
  dailyCtx, autoProjectApplied
}) {
  const { t, lang } = useTranslation();
  const dateLoc = lang === "sk" ? sk : lang === "en" ? enUS : de;
  const plannedIds = (dailyCtx?.planned_projects || []).map(p => p.id);
  const plannedSet = new Set(plannedIds);
  // Wenn geplante Projekte vorhanden sind → nur diese im Dropdown; sonst alle aktiven
  const activeProjects = projects.filter(p => p.is_active !== false && p.status === "aktiv");
  const dropdownProjects = plannedIds.length > 0
    ? activeProjects.filter(p => plannedSet.has(p.id))
    : activeProjects;
  const showProjectDropdown = dropdownProjects.length > 1;
  const singleProject = dropdownProjects.length === 1 ? dropdownProjects[0] : null;
  const clockedInTime = todayEntry?.clock_in_local || (clockedInAt ? format(clockedInAt, "HH:mm") : null);
  const hasValidProject = selectedProject || singleProject?.id;
  
  const getElapsedWorkTime = () => {
    if (!clockedInAt) return "00:00:00";
    const elapsed = Math.max(0, Math.floor((currentTime - clockedInAt) / 1000));
    const hours = Math.floor(elapsed / 3600);
    const minutes = Math.floor((elapsed % 3600) / 60);
    const seconds = elapsed % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };
  
  return (
    <div className="flex flex-col bg-[#0f1117] text-white px-4 pt-4 pb-5" data-testid="mobile-quick-mode">
      <div className="text-center mb-3">
        <p className="text-gray-400 text-sm">{format(currentTime, "EEEE, dd.MM.yyyy", { locale: dateLoc })}</p>
        <p className="text-[30px] font-bold text-white font-mono mt-0.5" data-testid="mobile-time">{format(currentTime, "HH:mm:ss")}</p>
      </div>
      
      <div className="text-center mb-2">
        {clockStatus ? (
          <span className="inline-flex items-center gap-1.5 text-green-400 font-medium text-sm">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            {t("clock.eingestempelt_seit")} {clockedInTime}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-red-400 font-medium text-sm">
            <span className="w-2 h-2 rounded-full bg-red-400" />
            {t("clock.nicht_eingestempelt", "Nicht eingestempelt")}
          </span>
        )}
      </div>
      
      {clockStatus && (
        <div className="text-center mb-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">{t("m.aktuelle_arbeitszeit")}</p>
          <p className="text-[22px] font-semibold text-green-400 font-mono" data-testid="mobile-elapsed-time">{getElapsedWorkTime()}</p>
        </div>
      )}
      
      {!clockStatus && <div className="mb-2" />}
      
      {!clockStatus && (
        <div className="mb-3">
          <label className="text-xs text-gray-500 mb-1 block font-medium uppercase tracking-wide">{t("m.projekt")}</label>
          {showProjectDropdown ? (
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger className="h-11 text-base bg-white/[0.06] border-white/10 text-white" data-testid="mobile-project-select">
                <SelectValue placeholder={t("m.projekt_waehlen")} />
              </SelectTrigger>
              <SelectContent>
                {dropdownProjects.map((p) => (
                  <SelectItem key={p.id} value={p.id} className="text-base py-3"><span className="truncate">{p.name}</span></SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : singleProject ? (
            <div className="h-11 flex items-center px-3 bg-white/[0.06] border border-white/10 rounded-md text-white text-sm">
              <MapPin className="w-4 h-4 mr-2 text-gray-500 flex-shrink-0" /><span className="truncate">{singleProject.name}</span>
            </div>
          ) : (
            <div className="h-11 flex items-center px-3 bg-white/[0.03] border border-white/10 rounded-md text-gray-500 text-sm">{t("m.kein_projekt")}</div>
          )}
          {autoProjectApplied && (
            <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-blue-300" data-testid="clock-auto-project-hint">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
              <span>Projekt aus Einsatzplanung übernommen</span>
            </div>
          )}
          {dailyCtx?.planned_projects?.length > 1 && (
            <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-amber-300" data-testid="clock-multi-project-hint">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
              <span>Mehrere Projekte für heute eingeplant</span>
            </div>
          )}
          {dailyCtx?.accommodation && (
            <div className="mt-2 flex items-center gap-1.5 text-[11px] text-purple-300" data-testid="clock-accommodation-hint">
              <span className="w-1.5 h-1.5 rounded-full bg-purple-400" />
              <span>Übernachtung erkannt: {dailyCtx.accommodation.name}</span>
            </div>
          )}
        </div>
      )}
      
      {clockStatus && selectedProject && (
        <div className="mb-3">
          <label className="text-xs text-gray-500 mb-1 block font-medium uppercase tracking-wide">{t("m.projekt")}</label>
          <div className="h-11 flex items-center px-3 bg-white/[0.04] border border-white/10 rounded-md text-gray-400 text-sm">
            <MapPin className="w-4 h-4 mr-2 text-gray-500 flex-shrink-0" />
            <span className="truncate">{projects.find(p => p.id === selectedProject)?.name || t("m.projekt")}</span>
          </div>
        </div>
      )}

      {/* Navigation zur Baustelle (kompakt) */}
      {(() => {
        const navShowEnabled = user?.nav_show_in_clock !== false;
        const proj = selectedProject ? projects.find(p => p.id === selectedProject) : null;
        const navUrl = proj ? buildNavUrl(proj) : null;
        if (!navUrl || !navShowEnabled) return null;
        return (
          <a href={navUrl} target="_blank" rel="noopener noreferrer"
            className="mb-3 w-full flex items-center justify-center gap-1.5 h-8 bg-blue-600/10 border border-blue-500/15 text-blue-400/80 rounded-lg text-xs font-medium active:bg-blue-600/20 transition-colors"
            data-testid="mobile-nav-to-site-btn"
          >
            <Navigation className="w-3 h-3" />Zur Baustelle
          </a>
        );
      })()}
      
      {user?.auto_pause_minutes > 0 && (
        <div className="mb-3 flex items-center gap-2 px-3 py-2 bg-amber-500/10 border border-amber-500/20 rounded-lg" data-testid="mobile-auto-pause-info">
          <Coffee className="w-4 h-4 text-amber-400 shrink-0" />
          <p className="text-xs text-amber-400 font-medium">
            {(user.auto_pause_min_hours ?? 0) > 0
              ? t("m.auto_pause_detail").replace("{min}", user.auto_pause_minutes).replace("{hours}", user.auto_pause_min_hours)
              : t("m.auto_pause_detail_no_min").replace("{min}", user.auto_pause_minutes)}
          </p>
        </div>
      )}
      
      <div className="mt-1">
        {!clockStatus ? (
          <>
            {!hasValidProject && <p className="text-center text-orange-400 text-sm mb-2 font-medium">{t("m.bitte_projekt")}</p>}
            <Button
              onClick={() => {
                if (singleProject && !selectedProject) { setSelectedProject(singleProject.id); setTimeout(() => handleClockIn(), 50); }
                else { handleClockIn(); }
              }}
              disabled={!hasValidProject}
              className="w-full h-14 bg-green-600 hover:bg-green-700 active:bg-green-800 disabled:bg-gray-700 disabled:cursor-not-allowed text-white text-xl font-bold uppercase tracking-wider rounded-xl"
              data-testid="mobile-clock-in-btn"
            >
              {t("m.einstempeln")}
            </Button>
          </>
        ) : (
          <Button onClick={handleClockOut} className="w-full h-14 bg-red-600 hover:bg-red-700 active:bg-red-800 text-white text-xl font-bold uppercase tracking-wider rounded-xl" data-testid="mobile-clock-out-btn">
            {t("m.ausstempeln")}
          </Button>
        )}
      </div>
      
      <p className="text-center text-xs text-gray-600 mt-4">{user?.name}</p>

      {todayEntry && todayEntry.clock_out && (
        <div className="mt-4 bg-white/[0.04] border border-white/8 rounded-lg p-3" data-testid="mobile-today-summary">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{t("clock.heutige_arbeitszeit")}</h4>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">{t("clock.einstempeln")}</span>
              <span className="font-mono text-gray-300">{toLocalHHMM(todayEntry.clock_in, todayEntry.clock_in_local)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">{t("clock.ausstempeln")}</span>
              <span className="font-mono text-gray-300">{toLocalHHMM(todayEntry.clock_out, todayEntry.clock_out_local)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">{t("clock.pause")}</span>
              <span className="font-mono text-gray-300">
                {Math.floor((todayEntry.total_break_seconds || 0) / 60)} min
                {todayEntry.auto_pause_applied && <span className="text-amber-400 ml-1 text-[10px]">{t("clock.auto")}</span>}
              </span>
            </div>
            <div className="flex justify-between pt-1.5 border-t border-white/10">
              <span className="text-gray-300 font-medium">{t("clock.netto")}</span>
              <span className="font-mono font-semibold text-green-400">{todayEntry.net_work_time}</span>
            </div>
          </div>
          {/* Fahrten in Zusammenfassung */}
        </div>
      )}

      {/* Navigation Popup nach Einstempeln */}
      {showNavPopup && navProject && (
        <NavPopup project={navProject} onClose={onCloseNavPopup} />
      )}
    </div>
  );
}
